import { api } from "../../lib";

export interface ReportReviewMatrixPrefix {
  id: string;
  code: string;
}

export interface ReportReviewMatrixPathologist {
  id: string;
  fullName: string;
  credentials: string | null;
}

export interface RelaxedReportReviewCombination {
  prefixId: string;
  pathologistUserId: string;
  relaxedAt: string;
  relaxedBy: { id: string; fullName: string } | null;
}

export interface ReportReviewMatrixData {
  revision: number;
  prefixes: ReportReviewMatrixPrefix[];
  pathologists: ReportReviewMatrixPathologist[];
  relaxedCombinations: RelaxedReportReviewCombination[];
  updatedAt: string;
  updatedBy: { id: string; fullName: string } | null;
}

export type ReportReviewSettingsTarget =
  | { type: "combination"; prefixId: string; pathologistUserId: string }
  | { type: "prefix"; prefixId: string }
  | { type: "pathologist"; pathologistUserId: string }
  | { type: "global" };

export interface UpdateReportReviewSettingsRequest {
  expectedRevision: number;
  reviewRequired: boolean;
  target: ReportReviewSettingsTarget;
}

export interface UpdateReportReviewSettingsResult {
  changed: boolean;
  revision: number;
  affectedCount: number;
  matrix: ReportReviewMatrixData;
}

interface ReportReviewSettingsHttpError {
  response?: {
    status?: number;
    data?: { code?: string; currentRevision?: number };
  };
}

export class ReportReviewSettingsConflictError extends Error {
  readonly currentRevision?: number;

  constructor(currentRevision?: number) {
    super(
      "These settings changed while you were editing. Review the latest matrix and try again."
    );
    this.name = "ReportReviewSettingsConflictError";
    this.currentRevision = currentRevision;
  }
}

function translateSettingsError(error: unknown): Error {
  const response = (error as ReportReviewSettingsHttpError).response;
  if (response?.status === 409 && response.data?.code === "REVISION_CONFLICT") {
    return new ReportReviewSettingsConflictError(response.data.currentRevision);
  }
  if (response?.status === 403) {
    return new Error(
      "You do not have permission to manage report review requirements."
    );
  }
  return new Error(
    "Report review requirements are unavailable. Please try again."
  );
}

export const reportReviewSettingsService = {
  get: async (): Promise<ReportReviewMatrixData> => {
    try {
      return await api.get<ReportReviewMatrixData>("/report-review-settings");
    } catch (error) {
      throw translateSettingsError(error);
    }
  },
  update: async (
    request: UpdateReportReviewSettingsRequest
  ): Promise<UpdateReportReviewSettingsResult> => {
    try {
      return await api.put<UpdateReportReviewSettingsResult>(
        "/report-review-settings",
        request
      );
    } catch (error) {
      throw translateSettingsError(error);
    }
  },
};
